import { Button } from '@/components/ui/button';
import { useEffect, useRef } from 'react';

export const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const unlockVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    const unlockVideo = unlockVideoRef.current;
    if (!video || !unlockVideo) return;

    // Imposta tutti gli attributi necessari per iOS Safari
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('x-webkit-airplay', 'allow');
    video.muted = true;
    video.playsInline = true;
    
    unlockVideo.setAttribute('webkit-playsinline', 'true');
    unlockVideo.setAttribute('playsinline', 'true');
    unlockVideo.muted = true;
    unlockVideo.playsInline = true;

    let hasPlayed = false;

    // Funzione per sbloccare la riproduzione video su iOS
    const unlockVideoPlayback = async () => {
      if (hasPlayed) return;
      
      try {
        // Prima prova con il video nascosto
        unlockVideo.currentTime = 0;
        await unlockVideo.play();
        unlockVideo.pause();
        
        // Poi prova con il video principale
        video.currentTime = 0;
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          hasPlayed = true;
          // Nascondi eventuali controlli nativi iOS
          video.controls = false;
        }
      } catch (error) {
        // Se fallisce, prova ancora con il video principale
        try {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            await playPromise;
            hasPlayed = true;
            video.controls = false;
          }
        } catch (e) {
          // Ignora errori silenziosamente
        }
      }
    };

    // Prova a far partire quando i dati sono caricati
    const handleVideoReady = async () => {
      if (!hasPlayed) {
        await unlockVideoPlayback();
      }
    };

    // Gestisci interazioni dell'utente (molto aggressivo per iOS)
    const handleUserInteraction = () => {
      if (!hasPlayed) {
        unlockVideoPlayback();
      }
    };

    // Event listeners per il video
    video.addEventListener('loadedmetadata', handleVideoReady);
    video.addEventListener('loadeddata', handleVideoReady);
    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('canplaythrough', handleVideoReady);
    
    // Cattura TUTTE le possibili interazioni utente
    const interactionEvents = [
      'touchstart', 'touchend', 'touchmove', 'touchcancel',
      'mousedown', 'mouseup', 'click',
      'scroll', 'wheel',
      'keydown', 'keyup',
      'pointerdown', 'pointerup'
    ];
    
    interactionEvents.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { passive: true, once: false });
      window.addEventListener(event, handleUserInteraction, { passive: true, once: false });
    });

    // Prova quando la pagina diventa visibile
    const handleVisibilityChange = () => {
      if (!document.hidden && !hasPlayed) {
        unlockVideoPlayback();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Prova immediatamente dopo un piccolo delay per iOS
    setTimeout(() => {
      unlockVideoPlayback();
    }, 100);

    // Prova anche quando la pagina è completamente caricata
    if (document.readyState === 'complete') {
      unlockVideoPlayback();
    } else {
      window.addEventListener('load', unlockVideoPlayback, { once: true });
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleVideoReady);
      video.removeEventListener('loadeddata', handleVideoReady);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('canplaythrough', handleVideoReady);
      interactionEvents.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
        window.removeEventListener(event, handleUserInteraction);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('load', unlockVideoPlayback);
    };
  }, []);

  return (
    <section className="relative h-screen w-full overflow-hidden" aria-label="Hero section">
      {/* Video nascosto per sbloccare la riproduzione su iOS */}
      <video
        ref={unlockVideoRef}
        src="/hero-vid.mp4"
        muted
        playsInline
        preload="none"
        className="absolute opacity-0 pointer-events-none"
        style={{ width: '1px', height: '1px' }}
        aria-hidden="true"
      />
      
      {/* Background Video with Overlay */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          src="/hero-vid.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/img/DSC09272 (2024-04-22T17_55_41.962).JPG"
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          className="h-full w-full object-cover"
          aria-label="Video di sfondo con braci ardenti"
          style={{ WebkitPlaysinline: true } as React.CSSProperties}
        />
        
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background z-[5]" />
        
        {/* Overlay invisibile per catturare il primo touch su iOS */}
        <div 
          className="absolute inset-0 z-[6] md:hidden"
          onTouchStart={(e) => {
            const video = videoRef.current;
            if (video && video.paused) {
              video.play().catch(() => {});
              e.currentTarget.style.display = 'none';
            }
          }}
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center pt-[73px] md:pt-0">
        <span className="mb-6 animate-fade-in text-sm font-light uppercase tracking-[0.3em] text-primary opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }} aria-label="Categoria">
          Store Online
        </span>
        <h1 className="sr-only">Brace e Sapori Store - Costate e Prodotti Tipici Pugliesi</h1>
        <p className="mb-10 max-w-xl animate-fade-up text-lg font-light text-muted-foreground opacity-0 md:text-xl" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
          Costate di alta qualità e prodotti tipici pugliesi
          <br />
          direttamente a casa tua.
        </p>
        <div className="animate-fade-up opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
          <Button variant="hero" size="lg" asChild>
            <a href="/catalogo" aria-label="Sfoglia il catalogo prodotti">Sfoglia il Catalogo</a>
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
        <div className="h-14 w-[1px] bg-gradient-to-b from-transparent via-primary to-primary" />
      </div>
    </section>
  );
};
